import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ShoppingCart, Package2 } from "lucide-react";
import { loadCart, saveCart } from "@/utils/clientCart";
import { toast } from "sonner";

const eur = (n) =>
  n == null ? "—" : n.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 });
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const num = (n) => (n ?? 0).toLocaleString("it-IT");

export default function ClientRecurringProducts({ clientId, destId = null }) {
  const { authAxios } = useAuth();
  const [data, setData] = useState({ items: [], hide_price: false });
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const destParam = destId ? `&dest_id=${destId}` : "";
      const r = await authAxios.get(`/client/me/recurring-products?sort=${sort}&limit=200${destParam}`);
      setData(r.data);
    } catch (e) {
      toast.error("Impossibile caricare gli articoli ricorrenti");
    } finally { setLoading(false); }
  }, [authAxios, sort, destId]);

  useEffect(() => { load(); }, [load]);

  const addToCart = (item) => {
    if (!clientId) {
      toast.error("Profilo non collegato ad un'anagrafica");
      return;
    }
    if (!item.product_id) {
      toast.error("Articolo non più disponibile a catalogo");
      return;
    }
    const cart = loadCart(clientId);
    const cur = cart[item.product_id] || {};
    const priceList = Number(item.price_list_oggi) || 0;
    const lastPaid = Number(item.ultimo_prezzo_pagato) || 0;
    // Per i contrattualizzati (hide_price) il prezzo non è disponibile lato cliente —
    // verrà risolto a server-side al momento dell'invio ordine; teniamo price_unit=0 ma valorizziamo nome/SKU.
    const priceUnit = lastPaid > 0 ? lastPaid : priceList;
    const priceSource = lastPaid > 0 ? "last_paid" : priceList > 0 ? "listino" : "server_resolve";
    cart[item.product_id] = {
      ...cur,
      product_id: item.product_id,
      product_name: item.name || "",
      product_sku: item.sku || "",
      unit: item.unit || "pz",
      iva_percentage: Number(cur.iva_percentage) || 10,
      price_unit: priceUnit,
      price_list: priceList,
      price_source: priceSource,
      quantity: (Number(cur.quantity) || 0) + 1,
      note: cur.note || "",
      sc1: 0, sc2: 0, sc3: 0, sc4: 0,
    };
    saveCart(clientId, cart);
    window.dispatchEvent(new CustomEvent("triolo-cart-changed", { detail: { clientId } }));
    toast.success(`${item.name} → carrello (+1)`, { duration: 1500 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento articoli...
      </div>
    );
  }

  if (!data.items.length) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-8 text-center" data-testid="recurring-empty">
        <Package2 className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
        <p className="text-zinc-300 font-semibold">Nessun articolo ricorrente</p>
        <p className="text-zinc-500 text-sm mt-1">I tuoi acquisti compariranno qui dopo i primi ordini.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="recurring-products-list">
      <div className="flex items-center justify-between gap-2">
        <p className="text-zinc-400 text-xs sm:text-sm">
          {data.items.length} articoli acquistati in passato
          {data.hide_price && <span className="ml-2 text-amber-300">· prezzi su richiesta</span>}
        </p>
        <div className="flex items-center gap-1.5 text-xs">
          <button
            onClick={() => setSort("recent")}
            data-testid="sort-recent"
            className={`px-2.5 py-1 rounded ${sort === "recent" ? "bg-orange-500 text-black font-semibold" : "bg-zinc-800 text-zinc-300"}`}
          >Più recenti</button>
          <button
            onClick={() => setSort("frequent")}
            data-testid="sort-frequent"
            className={`px-2.5 py-1 rounded ${sort === "frequent" ? "bg-orange-500 text-black font-semibold" : "bg-zinc-800 text-zinc-300"}`}
          >Più frequenti</button>
        </div>
      </div>

      <div className="space-y-2">
        {data.items.map((it) => (
          <Card key={it.sku} className="bg-zinc-900 border-zinc-800 p-3 flex items-center gap-3" data-testid={`recurring-row-${it.sku}`}>
            {it.image_url ? (
              <img src={it.image_url} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded object-cover shrink-0 bg-zinc-800" />
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded bg-zinc-800 shrink-0 flex items-center justify-center">
                <Package2 className="w-5 h-5 text-zinc-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-zinc-100 font-semibold text-sm truncate">{it.name}</p>
              <p className="text-zinc-500 text-[11px] font-mono">SKU {it.sku} · {date(it.ultima_data)} · {num(it.n_volte)} volte</p>
              {!data.hide_price && (
                <p className="text-emerald-400 text-sm font-bold mt-0.5">
                  {eur(it.ultimo_prezzo_pagato)} <span className="text-zinc-500 text-[11px] font-normal">/{it.unit}</span>
                </p>
              )}
            </div>
            {it.in_catalogo && clientId && (
              <Button
                size="sm"
                onClick={() => addToCart(it)}
                data-testid={`add-recurring-${it.sku}`}
                className="bg-orange-500 hover:bg-orange-600 text-black font-semibold shrink-0 h-9"
              >
                <ShoppingCart className="w-4 h-4" />
              </Button>
            )}
            {!it.in_catalogo && (
              <span className="text-[10px] text-zinc-500 italic shrink-0">non più<br />a catalogo</span>
            )}
          </Card>
        ))}
      </div>

      <Button
        variant="ghost"
        onClick={load}
        data-testid="recurring-refresh"
        className="w-full mt-2 text-zinc-400 hover:text-zinc-100 text-xs"
      >
        <RefreshCw className="w-3 h-3 mr-1.5" /> Aggiorna lista
      </Button>
    </div>
  );
}
